import type { ColumnSchema } from '@/components/crud/types';
import { SIM_TODAY } from '@/utils/time';

import { closureLabel } from '@/domain/complaint-closure';

/** 列顺序与表头文案对齐 docs/原材料《2026年前制程零件投诉跟进.xls》「投诉零件清单」 */
export const complaintRowColumns: ColumnSchema[] = [
  { type: 'index', label: '序号', width: 56, align: 'center', fixed: 'left' },
  { type: 'date', prop: 'date', label: '投诉日期', width: 112, timeDisplay: 'date' },
  { type: 'text', prop: 'deptC', label: '投诉部门/人', width: 108, align: 'center' },
  { type: 'text', prop: 'deptP', label: '生产部门', width: 96, align: 'center' },
  { type: 'text', prop: 'jz', label: '机种', width: 100, align: 'left' },
  { type: 'text', prop: 'pm', label: '品名', width: 100, align: 'center' },
  { type: 'text', prop: 'lh', label: '料号', width: 132, align: 'left' },
  { type: 'text', prop: 'yc', label: '异常投诉', minWidth: 160, align: 'left', flex: true },
  { type: 'text', prop: 'cause', label: '原因分析', minWidth: 140, align: 'left', flex: true },
  {
    type: 'text',
    prop: 'temp',
    label: '临时对策（不良品处理对策）',
    headerTitle: '临时对策',
    headerBadge: true,
    headerBadgeTip: '不良品处理对策',
    minWidth: 120,
    align: 'left',
    flex: true,
  },
  { type: 'text', prop: 'permanent', label: '永久对策', minWidth: 120, align: 'left', flex: true },
  { type: 'text', prop: 'fzr', label: '负责人', width: 88 },
  {
    type: 'tag',
    prop: 'closureStatus',
    label: '闭环状态',
    minWidth: 200,
    align: 'center',
    tagMap: {
      overdueReply: 'danger',
      awaitingReply: 'warning',
      defectPending: 'warning',
      temp: 'warning',
      verifying: 'primary',
      closed: 'success',
    },
    formatter: (_value, row) => closureLabel(row, SIM_TODAY),
  },
  {
    type: 'group',
    label: '异常单完成包括临时对策（3天）',
    headerTitle: '异常单完成',
    headerBadge: true,
    headerBadgeTip: '包括临时对策（3天）',
    align: 'center',
    children: [
      {
        type: 'text',
        prop: 'replyPlan',
        label: '计划完成时间',
        headerTitle: '计划完成时间',
        headerTip: '异常回复\n计划完成时间',
        width: 100,
        align: 'center',
      },
      {
        type: 'text',
        prop: 'replyActual',
        label: '实际回复时间',
        headerTitle: '实际回复时间',
        headerTip: '异常回复\n实际回复时间',
        width: 100,
        align: 'center',
      },
      {
        type: 'text',
        prop: 'replyOntime',
        label: '是否按时',
        headerTitle: '是否按时',
        headerTip: '异常回复\n是否按时',
        width: 88,
        align: 'center',
      },
    ],
  },
  {
    type: 'group',
    label: '不良品处理完成（3天）',
    headerTitle: '不良品处理完成（3天）',
    align: 'center',
    children: [
      {
        type: 'text',
        prop: 'defectPlan',
        label: '计划完成时间',
        headerTitle: '计划完成时间',
        headerTip: '不良品处理\n计划完成时间',
        width: 100,
        align: 'center',
      },
      {
        type: 'text',
        prop: 'defectActual',
        label: '实际完成时间',
        headerTitle: '实际完成时间',
        headerTip: '不良品处理\n实际完成时间',
        width: 100,
        align: 'center',
      },
      {
        type: 'text',
        prop: 'defectOntime',
        label: '是否按时',
        headerTitle: '是否按时',
        headerTip: '不良品处理\n是否按时',
        width: 88,
        align: 'center',
      },
    ],
  },
  {
    type: 'group',
    label: '对策结案预计7天或按工程回复时间',
    headerTitle: '对策结案',
    headerBadge: true,
    headerBadgeTip: '预计7天或按工程回复时间',
    align: 'center',
    children: [
      {
        type: 'text',
        prop: 'closePlan',
        label: '预计结案时间',
        headerTitle: '预计结案时间',
        headerTip: '结案\n预计结案时间',
        width: 100,
        align: 'center',
      },
      {
        type: 'text',
        prop: 'closeActual',
        label: '实际结案时间',
        headerTitle: '实际结案时间',
        headerTip: '结案\n实际结案时间',
        width: 100,
        align: 'center',
      },
      {
        type: 'text',
        prop: 'closeOntime',
        label: '是否按时',
        headerTitle: '是否按时',
        headerTip: '结案\n是否按时',
        width: 88,
        align: 'center',
      },
    ],
  },
  { type: 'text', prop: 'repeat', label: '是否重复异常', width: 112, align: 'center' },
  { type: 'text', prop: 'week', label: '周', width: 56, align: 'center' },
  { type: 'text', prop: 'month', label: '月', width: 56, align: 'center' },
];

/** 对齐交互原型「投诉台账」表格视图：扫一行能定位，其余字段在详情里 */
export const complaintFollowColumns: ColumnSchema[] = [
  { type: 'index', label: '序号', width: 56, align: 'center', fixed: 'left' },
  { type: 'date', prop: 'date', label: '投诉日', width: 112, timeDisplay: 'date' },
  { type: 'text', prop: 'jz', label: '机种', width: 100, align: 'left' },
  { type: 'text', prop: 'pm', label: '品名', width: 100, align: 'center' },
  { type: 'text', prop: 'lh', label: '料号', width: 132, align: 'left' },
  {
    type: 'text',
    prop: 'yc',
    label: '异常描述',
    minWidth: 160,
    align: 'left',
    flex: true,
    showOverflowTooltip: true,
  },
  {
    type: 'text',
    prop: 'deptC',
    label: '投诉→生产',
    minWidth: 140,
    align: 'left',
    formatter: (_value, row) => `${row.deptC || '—'} → ${row.deptP || '—'}`,
  },
  { type: 'text', prop: 'fzr', label: '负责人', width: 88 },
  { type: 'text', prop: 'replyPlan', label: '要求完成', width: 112, align: 'center' },
  { type: 'text', prop: 'replyActual', label: '实际回复', width: 112, align: 'center' },
  { type: 'text', prop: 'replyOntime', label: '按时', width: 72, align: 'center' },
  {
    type: 'tag',
    prop: 'followStatus',
    label: '状态',
    minWidth: 168,
    align: 'center',
    tagMap: {
      overdueReply: 'danger',
      awaitingReply: 'warning',
      defectPending: 'warning',
      temp: 'warning',
      verifying: 'primary',
      closed: 'success',
    },
    formatter: (_value, row) => closureLabel(row, SIM_TODAY),
  },
  {
    type: 'text',
    prop: 'overdueDays',
    label: '超期',
    width: 80,
    align: 'center',
    formatter: (value) => (value == null || value === '' ? '—' : `${value} 天`),
  },
];

export const importBatchColumns: ColumnSchema[] = [
  { type: 'date', prop: 'finishedAt', label: '时间', width: 142, timeDisplay: 'datetime' },
  {
    type: 'text',
    prop: 'groupKey',
    label: '年份',
    width: 110,
    align: 'center',
    formatter: (value: string) => (value ? `${value}年` : '—'),
  },
  {
    type: 'tag',
    prop: 'status',
    label: '状态',
    width: 72,
    tagMap: { success: 'success', failed: 'danger' },
    formatter: (value: string) => (value === 'success' ? '成功' : '失败'),
  },
  {
    type: 'text',
    prop: 'fileName',
    label: '文件',
    minWidth: 148,
    align: 'left',
  },
  {
    type: 'text',
    prop: 'rowCount',
    label: '行数',
    width: 52,
    align: 'center',
    formatter: (_value, row) => (row.status === 'success' ? String(row.rowCount) : '—'),
  },
  { type: 'custom', prop: 'remark', label: '备注', minWidth: 100, slotName: 'col-remark' },
];
